import { faCompressWide } from '@fortawesome/pro-regular-svg-icons';
import { faExpandWide } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Ramda from 'ramda';

import {
  Avatar as MuiAvatar,
  Box,
  IconButton,
  List,
  ListItem as MuiListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper as MuiPaper,
  Popover,
  Switch,
  Typography,
  MenuItem as MuiMenuItem,
  Divider,
  ButtonGroup,
  Button,
} from '@mui/material';
import { makeStyles, styled } from '@mui/styles';

// icons
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFeature } from '@growthbook/growthbook-react';
import { useActor } from '../../../../../components/hooks/useActor';
import { withShowable } from '../../../../_global/lib/withShowable';
import {
  setNewAgentExperienceAttributes,
  toggleTheme,
} from '../../../../../reducers/UiSettingsReducers';
import type { RootStore } from '../../../../../types';
import { usePersona } from '../../../../../components/hooks/usePersona';
import { selectGrowthbook } from '../../../../../store/selectors/ui-settings.selectors';
import Showable from '../../../../../components/Showable';
import {
  toggleFeatureFlags,
  toggleUKEnvFlag,
  toggleAUEnvFlag,
} from '../../../../../store/actions';
import { GROWTHBOOK_FEATURE_SETTINGS } from '../../../../../store/consts/ui-settings.store.consts';
import { usePlatformLanguage } from '../../../../../utils/hooks/usePlatformLanguage';
import { languages } from '../../../../../i18n/i18n.language-config';
import { FEATURE_FLAG_KEYS } from '../../../../../config/growthbook';
import { useEnvQuery } from '../../../../../queries/useEnvQuery';
import { ENVIRONMENTS } from '../../../../../types/common/env.dto';
import { DynamicFormsToggleListItem } from './AvatarMenuListItem/DynamicFormsToggleListItem';
import { SubmissionsViewToggleListItem } from './AvatarMenuListItem/SubmissionsViewToggleListItem';
import { REDACTED_FIELD } from '../../../../../config/logrocket';

const ListItem = withShowable(MuiListItem);
const MenuItem = withShowable(MuiMenuItem);

export const AgencyUserAvatarMenu = () => {
  const actor = useActor();
  const persona = usePersona();
  const env = useEnvQuery();
  const dispatch = useDispatch();
  const theme = useSelector((state: RootStore) => state.uiSettings.theme);
  const { featureFlagsEnabled, ukEnvironmentEnabled, auEnvironmentEnabled } =
    useSelector(selectGrowthbook);
  const menuItemClasses = useMenuItemStyles();
  const { new_agent_experience } = useSelector(selectGrowthbook);
  const region = usePlatformLanguage();
  const AGENCY_DASHBOARD_PREREQ = useFeature(
    FEATURE_FLAG_KEYS.AGENCY_DASHBOARD_PREREQ
  );

  const [anchor, setAnchor] = useState(null);

  function handleThemeChange() {
    dispatch(toggleTheme());
  }

  function handlePopoverOpen(event: any) {
    setAnchor(event.currentTarget);
  }

  function handlePopoverClose() {
    setAnchor(null);
  }

  function handleGrowthBookSwitch() {
    dispatch(toggleFeatureFlags());
  }

  function handleUKToggle() {
    dispatch(toggleUKEnvFlag());
  }

  function handleAUSToggle() {
    dispatch(toggleAUEnvFlag());
  }

  function handleAgencyDashboardGrowthbookSwitch() {
    dispatch(
      toggleFeatureFlags(GROWTHBOOK_FEATURE_SETTINGS.NEW_AGENT_EXPERIENCE)
    );
  }

  function handleLayoutChange(nextLayout: string) {
    return () => {
      dispatch(setNewAgentExperienceAttributes({ layout: nextLayout }));
    };
  }

  const open = Boolean(anchor);

  const isDevEnv = [
    ENVIRONMENTS.DEV_ENV_ID,
    ENVIRONMENTS.DEV_SPACE,
    ENVIRONMENTS.LOCAL,
  ].some(Ramda.equals(env.data?.envName));
  const isQaEnv = [ENVIRONMENTS.QA_ENV_ID].some(
    Ramda.equals(env.data?.envName)
  );
  const isStagingEnv = [ENVIRONMENTS.STAGING_ENV_ID].some(
    Ramda.equals(env.data?.envName)
  );

  return (
    <>
      <IconButton
        size="small"
        onClick={handlePopoverOpen}
        data-qa="BUTTON:user-avatar"
      >
        <Avatar
          src={actor.avatarUrl || undefined}
          style={{ width: '2.6rem', height: '2.6rem' }}
        >
          <Typography variant="caption">
            {actor.initials ||
              `${actor.firstName?.[0] || ''}${actor.lastName?.[0] || ''}`}
          </Typography>
        </Avatar>
      </IconButton>
      <Popover
        id="mouse-over-popover"
        open={open}
        anchorEl={anchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Paper>
          <List dense style={{ paddingTop: 0 }}>
            <ListItem className={REDACTED_FIELD}>
              <ListItemText
                disableTypography
                primary={
                  <div className="tertia-text contrast-text">
                    {actor.fullName}
                  </div>
                }
                secondary={
                  <>
                    <Typography className="normal-text">
                      {actor.account.fullName}
                    </Typography>
                    <Typography variant="body1" className="normal-text">
                      {actor.roleName}
                    </Typography>
                  </>
                }
              />
            </ListItem>

            <MenuItem
              data-qa="MENU_ITEM:edit-profile"
              component={Link}
              to={`${persona.basePath}/manage/me`}
              classes={menuItemClasses}
            >
              <Typography className="normal-text contrast-text">
                Edit profile
              </Typography>
            </MenuItem>

            <ListItem
              button
              data-qa="MENU_ITEM:Logout"
              to="/logout"
              component={Link}
            >
              <ListItemText
                primary="Logout"
                disableTypography
                className="contrast-text"
              />
              <ListItemSecondaryAction style={{ right: 0 }}>
                <Box display="flex" alignItems="center">
                  <ListItemIcon>
                    <ExitToAppIcon />
                  </ListItemIcon>
                </Box>
              </ListItemSecondaryAction>
            </ListItem>

            <Box pt={1} pb={1} pl={2} pr={2}>
              <Divider />
            </Box>

            <ListItem data-qa="MENU_ITEM:toggle-theme">
              <ListItemText
                primary={
                  <span
                    className="contrast-text"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {theme}
                  </span>
                }
                disableTypography
              />
              <ListItemSecondaryAction
                onClick={handleThemeChange}
                style={{ right: 0 }}
              >
                <Switch value={theme} checked={theme === 'light'} />
              </ListItemSecondaryAction>
            </ListItem>

            <Showable show={actor.isCowbell || isDevEnv || isQaEnv}>
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Feature Flags
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleGrowthBookSwitch}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={featureFlagsEnabled}
                    checked={featureFlagsEnabled}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>

            <Showable show={isDevEnv || isQaEnv}>
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Simulate UK env.
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleUKToggle}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={ukEnvironmentEnabled}
                    checked={ukEnvironmentEnabled}
                    disabled={auEnvironmentEnabled}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>

            <Showable show={isDevEnv || isQaEnv}>
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Simulate AU env.
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleAUSToggle}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={auEnvironmentEnabled}
                    checked={auEnvironmentEnabled}
                    disabled={ukEnvironmentEnabled}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>

            <Showable show={isDevEnv || isQaEnv || isStagingEnv}>
              <DynamicFormsToggleListItem />
              <SubmissionsViewToggleListItem />
            </Showable>

            <Showable
              show={AGENCY_DASHBOARD_PREREQ.on && region === languages['en-US']}
            >
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Latest Dashboard
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleAgencyDashboardGrowthbookSwitch}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={new_agent_experience}
                    checked={new_agent_experience}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>

            <ListItem data-qa="MENU_ITEM:change-layout">
              <ListItemText
                primary={
                  <span
                    className="contrast-text"
                    style={{ textTransform: 'capitalize' }}
                  >
                    Layout
                  </span>
                }
                disableTypography
              />
              <ButtonGroup>
                <Button
                  title="centered"
                  onClick={handleLayoutChange('compressed')}
                >
                  <FontAwesomeIcon icon={faCompressWide} />
                </Button>
                <Button title="full" onClick={handleLayoutChange('expanded')}>
                  <FontAwesomeIcon icon={faExpandWide} />
                </Button>
              </ButtonGroup>
            </ListItem>
          </List>
        </Paper>
      </Popover>
    </>
  );
};

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  color: theme.palette.common.white,

  background: theme.palette.common.cowbellBlue,
  width: '2rem',
  height: '2rem',
}));

const Paper = styled(MuiPaper)(({ theme }) => ({
  minWidth: 250,

  background: `${theme.palette.background.modal} !important`,
}));

const useMenuItemStyles = makeStyles((theme) => ({
  root: {
    '&:hover, &:focus': {
      backgroundColor: theme.palette.background.default,
    },
  },
}));
